import * as Sentry from '@sentry/browser';
import {assert} from './assert';
import {getPageViewParams} from './tracking/common';
import * as Tiara from './tracking/tiara';
import {getIsWebView} from './userAgent';
import {
  IS_CLIENT,
  IS_QA,
  IS_CBT_OR_PROD,
  VERSION,
  SENTRY_DSN,
} from '../constant';
import {getClientStore} from '../store';
import {ITorosInfo} from '../type';

export type ISendPageViewParam = Omit<Tiara.ITrackPageViewParam, 'user'> & {
  isSendExtra?: boolean; // 동일하게 extra 로그를 한 번 더 보낸다.
};
type ISendExtraParam = Omit<Tiara.ITrackExtraParam, 'user'>;
export type ISendEventParam = Omit<Tiara.ITrackEventParam, 'user'>;
export type ISendViewImpParam = Omit<Tiara.ITrackViewImpParam, 'user'>;
type ISendUsageParam = Omit<Tiara.ITrackUsageParam, 'user'>;

export function initTracking() {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');

  // 뉴티아라는 개발모드에서 데이터 수집 없이 모니터링만 가능하도록 세팅되므로 항상 초기화해준다
  Tiara.init();

  window.removeEventListener('message', receiveMessage);
  window.addEventListener('message', receiveMessage);
}

function receiveMessage(event: MessageEvent) {
  const {func, param} = event.data;
  if (window.kakaowebtoonWeb && func in window.kakaowebtoonWeb) {
    window.kakaowebtoonWeb[func as keyof typeof window.kakaowebtoonWeb](param);
  }
}

export function checkAndSendPageView() {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');
  const params = getPageViewParams();
  params && sendPageView(params);
}

export function sendPageView(params: ISendPageViewParam) {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');

  const isSendExtra = params.isSendExtra;
  delete params.isSendExtra;

  getIsReadyForSend()
    ? _sendPageView(params)
    : waitForInit({type: 'pageView', ...params});

  if (isSendExtra) {
    sendExtra(params);
  }
}

export function sendExtra(params: ISendExtraParam) {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');

  getIsReadyForSend()
    ? _sendExtra(params)
    : waitForInit({type: 'extra', ...params});
}

export function sendEvent(params: ISendEventParam) {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');

  getIsReadyForSend()
    ? _sendEvent(params)
    : waitForInit({type: 'event', ...params});
}

export function sendViewImp(params: ISendViewImpParam) {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');

  getIsReadyForSend()
    ? _sendViewImp(params)
    : waitForInit({type: 'viewImp', ...params});
}

function getIsReadyForSend() {
  return (
    getIsWebView() ||
    (Tiara.isInit && getClientStore()?.getState().auth.isFetchUserFinished)
  );
}

function _sendPageView(params: ISendPageViewParam) {
  if (getIsWebView()) {
    window.kakaowebtoonApp?.pageViewEvent?.(JSON.stringify(params));
  } else {
    Tiara.trackPageView({user: getUser(), ...params});
  }
}

function _sendExtra(params: ISendExtraParam) {
  if (getIsWebView()) {
    window.kakaowebtoonApp?.trackExtraEvent?.(JSON.stringify(params));
  } else {
    Tiara.trackExtra({user: getUser(), ...params});
  }
}

function _sendEvent(params: ISendEventParam) {
  if (getIsWebView()) {
    window.kakaowebtoonApp?.trackEvent?.(JSON.stringify(params));
  } else {
    Tiara.trackEvent({user: getUser(), ...params});
  }
}

function _sendViewImp(params: ISendViewImpParam) {
  if (getIsWebView()) {
    // TODO: webview에서 보내는 경우가 생기면 추가
  } else {
    Tiara.trackViewImp({user: getUser(), ...params});
  }
}

function _sendUsage(params: ISendUsageParam) {
  if (getIsWebView()) {
    // TODO: webview에서 보내는 경우가 생기면 추가
  } else {
    Tiara.trackUsage({user: getUser(), ...params});
  }
}

function getUser() {
  return getClientStore()?.getState().auth.user;
}

type ITrackerParam =
  | (ISendPageViewParam & {type: 'pageView'})
  | (ISendPageViewParam & {type: 'extra'})
  | (ISendEventParam & {type: 'event'})
  | (ISendViewImpParam & {type: 'viewImp'})
  | (ISendUsageParam & {type: 'usage'});
const waitQueueBeforeInit: ITrackerParam[] = [];
let timerId: number | undefined;
function waitForInit(param: ITrackerParam) {
  waitQueueBeforeInit.push(param);
  if (!timerId) {
    timerId = window.setInterval(() => {
      if (getIsReadyForSend()) {
        clearInterval(timerId);
        flushWaitQueue();
      }
      initTracking();
    }, 500);
  }
}

function flushWaitQueue() {
  while (waitQueueBeforeInit.length) {
    const params = waitQueueBeforeInit.shift()!;
    switch (params.type) {
      case 'pageView':
        _sendPageView(params);
        break;
      case 'extra':
        _sendExtra(params);
        break;
      case 'event':
        _sendEvent(params);
        break;
      case 'viewImp':
        _sendViewImp(params);
        break;
      case 'usage':
        _sendUsage(params);
        break;
    }
  }
}

export function initSentry() {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: VERSION,
  });
}

export function reportErrorSentry(error: Error) {
  if (!IS_CBT_OR_PROD && !IS_QA) return;
  Sentry.captureException(error);
}

export function getTorosCustomProps(
  torosInfo?: ITorosInfo,
  addtionalProps?: {[key: string]: any}
) {
  return {
    toros_imp_id: torosInfo?.torosImpId,
    toros_file_hash_key: torosInfo?.torosFileHashKey,
    toros_user_id_type: 'user_ex.user_id',
    ...addtionalProps,
  };
}

export function getTorosImpContents(
  torosInfo?: ITorosInfo,
  addtionalProps?: {[key: string]: any}
) {
  return {
    imp_id: torosInfo?.torosImpId,
    imp_provider: 'toros',
    imp_type: 'vimp',
    ...addtionalProps,
  };
}
